<!-- ADD TO TRUCK MODAL -->
<template>
  <b-modal
    :id="modalName"
    size="xl"
    role="document"
    title="Add Selected Order to Truck"
    v-model="modalIsVisible"
    @show="load"
    @hide="unload"
  >      
    <div v-if="loading" class="text-center">
      <b-spinner>Loading...</b-spinner> Loading
    </div>
    <div v-else>
      <div v-if="!isValid">
        <p>The following orders were selected to add to a truck</p>
        <p v-for="order in orders" :key="order.number">
          <strong class="d-inline-block mb-3">System #:&nbsp;</strong>
          {{ order.headerNumber }}
          <strong class="d-inline-block mb-3">CRN:&nbsp;</strong> {{ order.crn }}
          <strong class="d-inline-block mb-3">Facility:&nbsp;</strong>
          {{ order.facility || order.facilityCode }}
          <strong class="d-inline-block mb-3"
            >Scheduled Delivery Date:&nbsp;</strong
          >
          {{ formatDate(order.scheduledDeliveryDate) }}
          <strong class="d-inline-block mb-3">Zip:&nbsp;</strong> {{ order.zip }}
          <strong class="d-inline-block mb-3">Cubes:&nbsp;</strong>
          {{ formatDecimal(order.cubes) }}
        </p>
        <p>
          <strong class="d-inline-block mb-3">Total Cubes to Add:&nbsp;</strong>
          {{ totalCubesToAdd }}
        </p>
        <p>Unable to add the following orders on the same truck</p>
        <p
          v-for="preview in cannotAddPreviewResults"
          :key="preview.orderHeaderNumber"
        >
          <strong class="d-inline-block mb-3">SYS #:&nbsp;</strong>
          {{ preview.orderHeaderNumber }}
          <strong class="d-inline-block mb-3">Message:&nbsp;</strong>
          {{ preview.canAddMessage }}
        </p>
      </div>
      <div v-if="isValid">
        <div class="float-right">
          <b-button v-if="allowMap" @click="toggleOrdersMap()">
            <span v-if="ordersShowMap"> Hide Map </span>
            <span v-else> Show Map </span>
          </b-button>
        </div>
        <b-alert
          v-for="missingShipsWithOrder in ordersWithMissingShipsWithOrders"
          :key="missingShipsWithOrder.headerNumber"
          variant="warning"
          show
        >
          Ships with Orders not being added with order #{{
            missingShipsWithOrder.headerNumber
          }}
          -
          <span
            v-for="(
              headerNumber, index
            ) in missingShipsWithOrder.missingShipsWithOrderNumbers"
            :key="index"
            ><a href="#" @click="addOrder(headerNumber)">{{ headerNumber }}</a
            ><span
              v-if="
                index !=
                missingShipsWithOrder.missingShipsWithOrderNumbers.length - 1
              "
              >,
            </span></span
          >
          (<a
            href="#"
            @click="addAllShipsWithOrders(missingShipsWithOrder.headerNumber)"
            >Add All</a
          >)
        </b-alert>
        <p>The following orders will be add to the selected truck</p>
        <p v-for="order in orders" :key="order.number">
          <strong class="d-inline-block mb-3">System #:&nbsp;</strong>
          {{ order.headerNumber }}
          <strong class="d-inline-block mb-3">Received:&nbsp;</strong>
          {{ formatBool(order.isReceived) }}
          <strong class="d-inline-block mb-3">CRN:&nbsp;</strong> {{ order.crn }}
          <strong class="d-inline-block mb-3">Facility:&nbsp;</strong>
          {{ order.facility || order.facilityCode }}
          <strong class="d-inline-block mb-3"
            >Scheduled Delivery Date:&nbsp;</strong
          >
          {{ formatDate(order.scheduledDeliveryDate) }}
          <strong class="d-inline-block mb-3">Zip:&nbsp;</strong> {{ order.zip }}
          <strong class="d-inline-block mb-3">Cubes:&nbsp;</strong>
          {{ formatDecimal(order.cubes) }}
          <font-awesome-icon
            v-if="order.location"
            icon="map-marker"
            :color="getMapPinIconColor(order)"
            @click="selectOrdersMapOrder(order)"
            title="Select map pin"
          />
          <font-awesome-icon
            v-else
            icon="ban"
            title="No map location available"
          />
        </p>
        <p>
          <strong class="d-inline-block mb-3">Total Cubes to Add:&nbsp;</strong>
          {{ formatDecimal(totalCubesToAdd) }}
        </p>
        <GmapMap
          id="orders-map"
          v-if="ordersMapVisible"
          map-type-id="roadmap"
          class="orders-map"
          :options="getMapOptions()"
          :center="mapCenter"
          :zoom="mapZoomLevel"
        >
          <gmap-info-window
            :position="orderMapInfoWindowPos"
            :options="orderMapInfoWindowOptions"
            :opened="orderMapInfoWindowOpened"
            @closeclick="selectOrdersMapOrder(null)"
          />

          <GmapMarker
            v-for="(order, index) in ordersWithGps"
            :title="order.crn"
            :key="index"
            :position="getOrderPosition(order)"
            :clickable="true"
            :b-tr="false"
            :icon="getOrderMapIcon(order)"
            @click="selectOrdersMapOrder(order)"
          />
        </GmapMap>
        <p>
          Please select a Truck to add the order to from the list below by
          clicking on the Add button:
        </p>
        <div id="add-truck-listing-container" class="scrollable">
          <b-table-simple
            id="add-truck-listing"
            class="table table-sm table-striped table-bordered"
          >
            <b-thead class="text-nowrap">
              <b-tr>
                <b-th>Action</b-th>
                <b-th>Truck Name</b-th>
                <b-th>Facility</b-th>
                <b-th>Del Date</b-th>
                <b-th>Zip3s Served</b-th>
                <b-th>Status</b-th>
                <b-th>Driver</b-th>
                <b-th>Max Cubes</b-th>
                <b-th>Actual Cubes</b-th>
                <b-th>Avail Cubes</b-th>
                <b-th>Projected Avail</b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr v-if="loadingTrucks">
                <b-td colspan="11">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>Loading...</strong>
                </b-td>
              </b-tr>
              <b-tr v-if="!loadingTrucks && trucks != null && trucks.length == 0">
                <b-td colspan="11" class="text-center"
                  >No trucks available with delivery date and zip3.
                </b-td>
              </b-tr>
              <b-tr v-for="truck in recommendedTrucks" :key="truck.id">
                <b-td class="text-center">
                  <a
                    v-if="!adding"
                    class="toolbutton mr-1"
                    title="Select Truck"
                    href="#"
                    @click.prevent="addToTruck(truck, false)"
                  >
                    <font-awesome-icon icon="plus-circle" />
                  </a>
                  <a
                    v-if="!adding"
                    class="toolbutton mr-1"
                    title="Truck Map"
                    href="#"
                    @click.prevent="showTruckMap(truck)"
                  >
                    <font-awesome-icon icon="eye" />
                  </a>
                  <span v-else>
                    <b-spinner small />
                  </span>
                </b-td>
                <b-td>
                  {{ truck.name }}
                  <font-awesome-icon icon="star" class="text-success" />
                </b-td>
                <b-td v-text="truck.facilityCode" />
                <b-td v-text="formatDate(truck.deliveryDate)" />
                <b-td v-text="truck.zip3sServiced.join(', ')" />
                <b-td v-text="truck.status" />
                <b-td v-text="truck.driverName" />
                <b-td v-text="formatDecimal(truck.maxCubes)" class="text-right" />
                <b-td
                  v-text="formatDecimal(truck.cubesUsed)"
                  class="text-right"
                />
                <b-td
                  v-text="formatDecimal(truck.availableCubes)"
                  :class="getCubeWarningClass(truck)"
                />
                <b-td
                  v-text="formatDecimal(truck.availableCubes - totalCubesToAdd)"
                  :class="getProjectedCubeWarningClass(truck)"
                />
              </b-tr>
              <b-tr v-for="truck in nonrecommededTrucks" :key="truck.id">
                <b-td class="text-center">
                  <a
                    v-if="!adding"
                    class="toolbutton mr-1"
                    title="Select Truck"
                    href="#"
                    @click.prevent="addToTruck(truck, false)"
                  >
                    <font-awesome-icon icon="plus-circle" />
                  </a>
                  <a
                    v-if="!adding"
                    class="toolbutton mr-1"
                    title="Truck Map"
                    href="#"
                    @click.prevent="showTruckMap(truck)"
                  >
                    <font-awesome-icon icon="eye" />
                  </a>
                  <span v-else>
                    <b-spinner small />
                  </span>
                </b-td>
                <b-td>
                  {{ truck.name }}
                </b-td>
                <b-td v-text="truck.facilityCode" />
                <b-td v-text="formatDate(truck.deliveryDate)" />
                <b-td v-text="truck.zip3sServiced.join(', ')" />
                <b-td v-text="truck.status" />
                <b-td v-text="truck.driverName" />
                <b-td v-text="formatDecimal(truck.maxCubes)" class="text-right" />
                <b-td
                  v-text="formatDecimal(truck.cubesUsed)"
                  class="text-right"
                />
                <b-td
                  v-text="formatDecimal(truck.availableCubes)"
                  :class="getCubeWarningClass(truck)"
                />
                <b-td
                  v-text="formatDecimal(truck.availableCubes - totalCubesToAdd)"
                  :class="getProjectedCubeWarningClass(truck)"
                />
              </b-tr>
            </b-tbody>
          </b-table-simple>
          <TruckOrderViewModal
            v-if="selectedTruck"
            :truck="selectedTruck"
            @addOrderToTruck="addToTruck"
          />
          <b-modal
            id="addToTruckPromptModal"
            v-model="addToTruckPromptModalIsVisible"
            title="Add To Truck Zip Mismatch"
            ok-title="Yes"
            cancel-title="No"
            @ok="addToTruck(promptTruck, true)"
            @cancel="cancelPrompt()"
          >
            The truck zip3/zipcodes ({{
              promptTruck ? promptTruck.zip3sServiced.join(", ") : ""
            }}) do not match the order(s). Are you sure you want add the order(s)
            to this truck?
          </b-modal>
          <b-modal
            id="truckMapModal"
            size="xl"
            v-if="mapTruck"
            v-model="truckShowMap"
            :title="mapTruck.name"
            ok-only
            ok-title="Close"
            @ok="hideTruckMap()"
          >
            <b-table-simple
              v-if="mapTruckOrders"
              class="
                table table-sm table-striped table-bordered table-hover
                dataTable
                no-footer
              "
              role="grid"
            >
              <b-thead class="text-nowrap">
                <b-tr role="row">
                  <b-th class="table-b-table-default text-center"> </b-th>
                  <b-th class="table-b-table-default"> SYS # </b-th>
                  <b-th class="table-b-table-default"> Recv </b-th>
                  <b-th class="table-b-table-default"> Mfg </b-th>
                  <b-th class="table-b-table-default"> Type </b-th>
                  <b-th class="table-b-table-default"> Address </b-th>
                  <b-th class="table-b-table-default"> Remarks </b-th>
                  <b-th class="table-b-table-default"> Cubes </b-th>
                </b-tr>
              </b-thead>
              <b-tbody v-if="mapTruckOrders.length > 0">
                <b-tr
                  v-for="(order, index) in mapTruckOrders"
                  :key="order.orderId"
                  role="row"
                >
                  <b-td>{{ index + 1 }}</b-td>
                  <b-td>{{
                    order.orderHeaderNumber ? order.orderHeaderNumber : "Non-CDS"
                  }}</b-td>
                  <b-td>{{ formatBool(order.isReceived) }}</b-td>
                  <b-td>{{ order.manufacturer }}</b-td>
                  <b-td>{{ order.deliveryType }}</b-td>
                  <b-td>
                    <pre class="address">{{
                      order.stopParty.name +
                      "\n" +
                      formatAddress(order.stopParty.address)
                    }}</pre>
                  </b-td>
                  <b-td>{{ order.remarks }}</b-td>
                  <b-td>{{ formatDecimal(order.cubes) }}</b-td>
                </b-tr>
              </b-tbody>
              <b-tbody v-else>
                <b-tr>
                  <b-td colspan="7"> There are no orders on this truck. </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
            <TruckMap
              v-if="mapTruckOrders && mapTruckOrders.length > 0"
              :truck="mapTruck"
            />
          </b-modal>
        </div>
      </div>
    </div>
    <template v-slot:modal-footer class="justify-content-center">
      <b-button variant="primary" @click="hideModal"> Close </b-button>
    </template>
  </b-modal>
</template>
<!-- END ADD TO TRUCK MODAL -->
<script>
import {
  formatDate,
  getOrdersMapLocation,
  formatDecimal,
  formatAddress,
  formatBool,
} from "../../../utils";
import { mapActions, mapState } from "vuex";
import TruckMap from "../../../components/truck/TruckMap.vue";
import TruckOrderViewModal from "../../../components/widgets/Modals/TruckOrderViewModal";
import mixin from "./mixin";
import notifications from "@/lib/notifications";
import {
  PreviewAddOrderToTruckRequest,
  PreviewAddOrderToTruckResource,
} from "../../../models/api/trucks/addOrder";
import { AddOrderToTruckModel } from "@/models/ui/addOrderToTruck";
import { length } from "vee-validate/dist/rules";
import { getTruckCubeWarningLevel } from "@/store/modules/truck/helpers";
import * as modals from "@/lib/modals";
import { getModule } from "vuex-module-decorators";
import { SystemModule, TruckModule } from "@/store/modules";
import { GREEN_MAP_PIN_ICON, MAP_STYLES, RED_MAP_PIN_ICON } from "@/consts";

const systemModule = getModule(SystemModule);
const truckModule = getModule(TruckModule);

export default {
  name: modals.addOrderToTruckModal,
  mixins: [mixin],
  components: {
    TruckOrderViewModal,
    TruckMap,
  },
  data() {
    return {
      allowMap: true,
      ordersShowMap: false,
      selectedMapOrder: null,
      orderMapInfoWindowOpened: false,
      orderMapInfoWindowOptions: null,
      orderMapInfoWindowPos: null,
      mapCenter: null,
      mapZoomLevel: 10,
      selectedTruck: null,
      loadingTrucks: false,
      loading: false,
      trucks: [],
      orders: [],
      previewResults: [],
      isValid: true,
      adding: false,
      promptTruck: null,
      addToTruckPromptModalIsVisible: false,
      truckShowMap: false,
      mapTruck: null,
    };
  },
  computed: {
    ...mapState({
      truckData: (state) => state.truck.data,
    }),
    ...mapState("order", {
      orderData: (state) => state.data,
    }),
    ordersMapVisible() {
      return this.allowMap && this.ordersShowMap;
    },
    ordersWithMissingShipsWithOrders() {
      return this.orders.filter(
        (x) =>
          x.missingShipsWithOrderNumbers &&
          x.missingShipsWithOrderNumbers.length > 0
      );
    },
    ordersWithGps() {
      return this.orders.filter((x) => x.location);
    },
    cannotAddPreviewResults() {
      return this.previewResults.filter((x) => !x.canAdd);
    },
    orderZip3s() {
      let zip3s = [];
      this.orders.forEach((element) => {
        if (element.zip3 && zip3s.filter((x) => x == element.zip3))
          zip3s.push(element.zip3);
      });
      return zip3s;
    },
    orderZips() {
      let zips = [];
      this.orders.forEach((element) => {
        if (element.zip && zips.filter((x) => x == element.zip))
          zips.push(element.zip);
      });
      return zips;
    },
    totalCubesToAdd() {
      let totalCubes = 0;
      this.orders.forEach((element) => {
        if (element.cubes) totalCubes += element.cubes;
      });
      return totalCubes;
    },
    recommendedTrucks() {
      return this.trucks.filter((x) => this.isRecommendedTruck(x));
    },
    nonrecommededTrucks() {
      return this.trucks.filter((x) => !this.isRecommendedTruck(x));
    },
    mapTruckOrders() {
      return this.mapTruck ? this.mapTruck.orders : null;
    },
  },
  methods: {
    pushOrder(order) {
      let previewOrder = this.previewResults.find(
        (x) => x.orderHeaderNumber == order.headerNumber
      );
      this.orders.push({
        headerNumber: order.headerNumber,
        crn: order.crn,
        zip3: this.getZip3(order, previewOrder),
        zip: order.zip
          ? order.zip
          : order.consignee && order.consignee.address
          ? order.consignee.address.zip
          : null,
        facility: order.facility || order.facilityCode,
        scheduledDeliveryDate: order.scheduledDeliveryDate,
        missingShipsWithOrderNumbers: previewOrder.missingShipsWithHeaderNumbers
          ? previewOrder.missingShipsWithHeaderNumbers
          : null,
        cubes: order.cubes,
        stopParty: previewOrder.stopParty,
        location: previewOrder.location,
        isReceived: order.received ? order.received : order.isReceived,
      });
      var ordersWithMissingShipsWithHeaderNumbers =
        this.ordersWithMissingShipsWithOrders.filter(
          (x) => x.missingShipsWithOrderNumbers.indexOf(order.headerNumber) > -1
        );
      for (
        let index = 0;
        index < ordersWithMissingShipsWithHeaderNumbers.length;
        index++
      ) {
        let shipsWithOrder = ordersWithMissingShipsWithHeaderNumbers[index];
        let removeIndex = shipsWithOrder.missingShipsWithOrderNumbers.indexOf(
          order.headerNumber
        );
        shipsWithOrder.missingShipsWithOrderNumbers.splice(removeIndex, 1);
      }
    },
    getZip3(order, previewOrder) {
      var zip3 =
        previewOrder && previewOrder.stopParty && previewOrder.stopParty.address
          ? previewOrder.stopParty.address.zip.substring(0, 3)
          : order.zip3
          ? order.zip3
          : order.consignee && order.consignee.address
          ? order.consignee.address.zip.substring(0, 3)
          : null;
      var zip =
        previewOrder && previewOrder.stopParty && previewOrder.stopParty.address
          ? previewOrder.stopParty.address.zip
          : order.zip
          ? order.zip
          : order.consignee && order.consignee.address
          ? order.consignee.address.zip
          : null;
      var facility = order.facility || order.facilityCode;

      let result = systemModule.zip3Overrides.find(
        (x) => x.facilityCode === facility && x.zipCode === zip.substring(0, 5)
      );
      return result ? result.overrideZip3 : zip3;
    },
    async updatePreviewResults() {
      const request = {
        orderNumbers: this.selectedItems.map((x) => x.headerNumber || x.number),
      };

      this.previewResults = [];
      try {
        this.previewResults = await this.previewAddOrderToTruck(request);
      } catch (ex) {
        this.addActionResult({
          number: (this.selectedItems.length > 0 && this.selectedItems[i]) ? this.selectedItems[i].number : "",
          success: false,
          message: `Failed to add to truck. Failed to retreive ships with order information.`,
        });
        this.isValid = false;
        return;
      }
    },
    async load() {
      this.orders = [];
      this.trucks = [];
      this.allowMap = false;
      this.ordersShowMap = false;
      this.mapCenter = null;
      this.mapZoomLevel = 10;
      this.loadingTrucks = true;
      this.loading = true;
      this.selectedMapOrder = null;
      this.mapTruck = null;
      this.show();

      await this.updatePreviewResults();

      let currentOrder = this.selectedItems[0];
      let facility = currentOrder?.facility
        ? currentOrder?.facility
        : currentOrder?.facilityCode;
      let deliveryDate = currentOrder?.scheduledDeliveryDate;

      for (var i = 0; i < this.selectedItems.length; i++) {
        this.pushOrder(this.selectedItems[i]);
      }

      this.isValid = this.previewResults.find((x) => !x.canAdd) == null;
      this.loading = false;

      if (this.isValid) {
        try {
          let response = await this.getAvailableTrucksForFacilityAndDate({
            facility: facility,
            deliveryDate: deliveryDate,
          });
          this.trucks = response;
        } finally {
          this.loadingTrucks = false;
        }

        if (this.ordersWithGps.length > 0) {
          let mapInfo = getOrdersMapLocation(this.ordersWithGps);
          this.mapCenter = { lat: mapInfo.latCenter, lng: mapInfo.lngCenter };
          this.mapZoomLevel = mapInfo.zoomLevel;
          this.allowMap = true;
        }
      }
    },
    unload() {
      this.orders = [];
      this.trucks = [];
      this.allowMap = false;
      this.ordersShowMap = false;
      this.mapCenter = null;
      this.mapZoomLevel = 10;
      this.loadingTrucks = false;
      this.loading = false;
      this.selectedMapOrder = null;
      this.mapTruck = null;
    },
    async createTruck() {
      let firstOrder = this.selectedItems[0];
      let newTruckId = null;
      try {
        let truckCreationResponse = await this.createTruckForOrder(
          firstOrder.number
        );
        this.addActionResult({
          number: firstOrder.number,
          success: true,
          message: `Order ${firstOrder.number} added to new truck.`,
        });
        newTruckId = truckCreationResponse.data.truckId;
      } catch (error) {
        this.addActionResult({
          number: firstOrder.number,
          success: false,
          message: `Failed to create new truck for order. ${error.response.data.message}`,
          errors:
            error.response.data && error.response.data.errors
              ? error.response.data.errors
              : null,
          errorMessages:
            error.response.data && error.response.data.errorMessages
              ? error.response.data.errorMessages
              : null,
        });
        return;
      }
      for (let x = 1; x < this.selectedItems.length; x++) {
        await this.doAddOrderToTruck(this.selectedItems[x], {
          id: newTruckId,
          name: "",
        });
      }
      this.hideModal();
    },
    getCubeWarningClass(truck) {
      let warningLevel = getTruckCubeWarningLevel(
        truck.cubesUsed,
        truck.maxCubes
      );
      return "text-right truck-cube-warning-" + warningLevel.toString();
    },
    getProjectedCubeWarningClass(truck) {
      let warningLevel = getTruckCubeWarningLevel(
        truck.cubesUsed + this.totalCubesToAdd,
        truck.maxCubes
      );
      return "text-right truck-cube-warning-" + warningLevel.toString();
    },
    switchToTruckOrderView(truck) {
      let payload = {
        truck: truck,
        orders: this.selectedItems,
      };

      this.showTruckOrderViewModal(payload);
    },
    async addToTruck(truck, skipPrompt) {
      this.adding = true;
      if (skipPrompt || this.isRecommendedTruck(truck)) {
        this.promptTruck = null;
        this.addToTruckPromptModalIsVisible = false;
        for (let x = 0; x < this.orders.length; x++) {
          await this.doAddOrderToTruck(this.orders[x], truck);
        }
        this.adding = false;
        await this.refreshOrders();
        this.hideModal();
      } else {
        this.promptTruck = truck;
        this.addToTruckPromptModalIsVisible = true;
      }
    },
    async doAddOrderToTruck(selectedOrder, truck) {
      let action = {
        orderNumber: selectedOrder.headerNumber,
        truckId: truck.id,
      };
      try {
        await this.addOrderToTruck(action);
        this.addActionResult({
          number: selectedOrder.headerNumber,
          success: true,
          message: `Order ${selectedOrder.headerNumber} added to truck ${truck.name}.`,
        });
      } catch (error) {
        this.addActionResult({
          number: selectedOrder.headerNumber,
          success: false,
          message: error.response.data.message,
          errors:
            error.response.data && error.response.data.errors
              ? error.response.data.errors
              : null,
          errorMessages:
            error.response.data && error.response.data.errorMessages
              ? error.response.data.errorMessages
              : null,
        });
      }
    },
    cancelPrompt() {
      this.promptTruck = null;
      this.addToTruckPromptModalIsVisible = false;
      this.adding = false;
    },
    async showTruckMap(truck) {
      this.mapTruck = await truckModule.getTruck(truck.id);
      this.truckShowMap = true;
    },
    hideTruckMap() {
      this.mapTruck = null;
      this.truckShowMap = false;
    },
    formatDate(value) {
      return formatDate(value);
    },
    formatAddress(address) {
      return formatAddress(address);
    },
    formatDecimal(value) {
      return formatDecimal(value);
    },
    formatBool(value) {
      return formatBool(value);
    },
    async addOrder(headerNumber) {
      if (this.orders.filter((x) => x.headerNumber == headerNumber).length > 0)
        return;

      this.loading = true;

      var getOrdersRequest = {
        orderNumber: headerNumber,
        pageNumber: 1,
        pageSize: 1,
      };

      let response = await this.getOrders(getOrdersRequest);

      if (response.items && response.items.length > 0) {
        let selectedItem = response.items.map((x) => ({
          headerNumber: x.headerNumber,
          crn: x.crn,
          scheduledDeliveryDate: x.scheduledDeliveryDate,
          zip3:
            x.consignee && x.consignee.address
              ? x.consignee.address.zip.substring(0, 3)
              : null,
          zip:
            x.consignee && x.consignee.address ? x.consignee.address.zip : null,
          consignee: x.consignee,
          facility: x.facility || x.facilityCode,
          cubes: x.cubes
        }))[0];

        if (
          this.selectedItems.filter(
            (x) => x.headerNumber == selectedItem.headerNumber
          ).length == 0
        )
          this.addSelectedItem(selectedItem);

        await this.updatePreviewResults();
        this.loading = false;

        this.pushOrder(selectedItem);
      }
    },
    async addAllShipsWithOrders(headerNumber) {
      if (this.orders.filter((x) => x.headerNumber == headerNumber).length < 1)
        return;

      var addedOrders = [];

      this.loading = true;

      var getOrdersRequest = {
        shipsWithOrderNumber: headerNumber,
        pageNumber: 1,
        pageSize: 200,
      };

      let response = await this.getOrders(getOrdersRequest);

      if (response.items && response.items.length > 0) {
        for (var i = 0; i < response.items.length; i++) {
          let selectedItem = response.items.map((x) => ({
            headerNumber: x.headerNumber,
            crn: x.crn,
            facility: x.facilityCode || x.facility,
            scheduledDeliveryDate: x.scheduledDeliveryDate,
            zip3:
              x.consignee && x.consignee.address
                ? x.consignee.address.zip.substring(0, 3)
                : null,
            zip:
              x.consignee && x.consignee.address
                ? x.consignee.address.zip
                : null,
            consignee: x.consignee,
            facility: x.facility || x.facilityCode,
            cubes: x.cubes
          }))[i];

          if (
            this.orders.filter(
              (x) => x.headerNumber == selectedItem.headerNumber
            ).length == 0
          ) {
            addedOrders.push(selectedItem);

            if (
              this.selectedItems.filter(
                (x) => x.headerNumber == selectedItem.headerNumber
              ).length == 0
            )
              this.addSelectedItem(selectedItem);
          }
        }

        if (addedOrders && addedOrders.length > 0) {
          await this.updatePreviewResults();
          this.loading = false;

          for (var i = 0; i < addedOrders.length; i++) {
            this.pushOrder(addedOrders[i]);
          }
        }
      }
    },
    isRecommendedTruck(truck) {
      let orderZips = this.orderZips;
      let orderZip3s = this.orderZip3s;
      if (truck.zip3sServiced && orderZips) {
        return (
          orderZips.every((x) => truck.zip3sServiced.indexOf(x) >= 0) ||
          orderZip3s.every((x) => truck.zip3sServiced.indexOf(x) >= 0)
        );
      }
      return false;
    },
    getOrderPosition(order) {
      if (order && order.location)
        return { lat: order.location.latitude, lng: order.location.longitude };
      return null;
    },
    getOrderMapIcon(order) {
      if (order && order.location && order.location.isZipCodeLocation == true)
        return RED_MAP_PIN_ICON;
      return GREEN_MAP_PIN_ICON;
    },
    selectOrdersMapOrder(order) {
      this.ordersShowMap = this.allowMap;
      this.selectedMapOrder = order;
      this.orderMapInfoWindowOpened = order != null;
      this.orderMapInfoWindowOptions = this.getOrderMapInfoWindowOptions();
      this.orderMapInfoWindowPos = this.getOrderPosition(order);
    },
    getOrderMapInfoWindowOptions() {
      if (this.selectedMapOrder) {
        return {
          content:
            "<b>SYS #:</b> " +
            this.selectedMapOrder.headerNumber +
            " <b>CRN:</b> " +
            this.selectedMapOrder.crn +
            " <b>Cubes:</b> " +
            this.selectedMapOrder.cubes +
            " <b>Received:</b> " +
            formatBool(this.selectedMapOrder.isReceived) +
            "<br/>" +
            this.selectedMapOrder.stopParty.name +
            "<br/>" +
            this.selectedMapOrder.stopParty.address.streetLine1 +
            "<br/>" +
            this.selectedMapOrder.stopParty.address.city +
            ", " +
            this.selectedMapOrder.stopParty.address.state +
            " " +
            this.selectedMapOrder.stopParty.address.zip,
        };
      }
      return null;
    },
    getMapOptions() {
      return MAP_STYLES;
    },
    toggleOrdersMap() {
      this.ordersShowMap = !this.ordersShowMap;
    },
    getMapPinIconColor(order) {
      if (order && order.location && order.location.isZipCodeLocation == true)
        return "red";
      return "green";
    },
    ...mapActions("truck", {
      addOrderToTruck: "addOrderToTruck",
      showTruckOrderViewModal: "showTruckOrderViewModal",
      getAvailableTrucksForFacilityAndDate:
        "getAvailableTrucksForFacilityAndDate",
      previewAddOrderToTruck: "previewAddOrderToTruck",
    }),
    ...mapActions("order", {
      createTruckForOrder: "createTruckForOrder",
      getOrders: "getOrders",
    }),
  },
};
</script><style scoped>
.orders-map {
  width: 100%;
  height: 500px;
}
.autosize-map {
  width: 100%;
  height: 700px;
}
</style>
